import React, { useState } from 'react';
import { useShop } from '../../context/ShopContext';
import { defaultTestimonials } from '../../data/products';
import { 
  Star, 
  Plus, 
  Trash2, 
  Edit3, 
  Quote, 
  CheckCircle2, 
  X, 
  RotateCcw, 
  Sparkles,
  Upload
} from 'lucide-react';

export default function AdminTestimonialsManager() {
  const { testimonialsData, addTestimonial, updateTestimonial, deleteTestimonial, addToast } = useShop();

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const initialForm = {
    name: '',
    role: 'Verified Patron, Jaipur',
    quote: '',
    rating: 5,
    product: 'Handcrafted Heritage Jewellery',
    avatar: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&w=200&q=80',
    isVerified: true
  };

  const [form, setForm] = useState(initialForm);

  const openAddModal = () => {
    setEditingId(null);
    setForm(initialForm);
    setModalOpen(true);
  };

  const openEditModal = (rev) => {
    setEditingId(rev.id);
    setForm({ ...rev });
    setModalOpen(true);
  };

  const handleSave = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.quote.trim()) {
      addToast('Please enter patron name and testimonial review quote', 'error');
      return;
    }

    if (editingId) {
      updateTestimonial(editingId, form);
    } else {
      addTestimonial(form);
    }
    setModalOpen(false);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      setForm((prev) => ({ ...prev, avatar: event.target?.result }));
      addToast('Patron photo uploaded!', 'info');
    };
    reader.readAsDataURL(file);
  };

  return (
    <div style={{ maxWidth: '1000px' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '1rem',
        marginBottom: '2rem'
      }}>
        <div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', color: 'var(--gold-400)', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', marginBottom: '0.3rem' }}>
            <Sparkles size={13} /> Social Proof & Reputation CMS
          </div>
          <h2 style={{ fontSize: '1.6rem', fontWeight: 800, color: '#fff' }}>
            Bride Stories & <span className="text-gold-gradient">Customer Reviews</span>
          </h2>
          <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
            Publish verified buyer feedback, destination wedding testimonials, and Google 5-star quotes.
          </p>
        </div>

        <button
          onClick={openAddModal}
          className="btn btn-gold btn-sm"
          style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', fontSize: '0.82rem' }}
        >
          <Plus size={16} />
          <span>Add Patron Testimonial</span>
        </button>
      </div>

      {/* Testimonials Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
        gap: '1.25rem'
      }}>
        {testimonialsData.map((rev) => (
          <div
            key={rev.id}
            style={{
              background: '#13131e',
              border: '1px solid var(--border-subtle)',
              borderRadius: 'var(--radius-md)',
              padding: '1.5rem',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              gap: '1rem'
            }}
          >
            <div>
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.8rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <img
                    src={rev.avatar}
                    alt={rev.name}
                    style={{ width: '45px', height: '45px', borderRadius: '50%', objectFit: 'cover', border: '1px solid var(--border-gold)' }}
                  />
                  <div>
                    <div style={{ fontWeight: 800, color: '#fff', fontSize: '0.92rem' }}>{rev.name}</div>
                    <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)' }}>{rev.role}</div>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '2px' }}>
                  {[...Array(Number(rev.rating) || 5)].map((_, i) => (
                    <Star key={i} size={14} fill="var(--gold-400)" color="var(--gold-400)" />
                  ))}
                </div>
              </div>

              {/* Quote */}
              <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', lineHeight: 1.6, fontStyle: 'italic', margin: 0 }}>
                "{rev.quote}"
              </p>

              {/* Product mention */}
              {rev.product && (
                <div style={{
                  marginTop: '0.8rem',
                  fontSize: '0.72rem',
                  color: 'var(--gold-400)',
                  background: 'rgba(212, 175, 55, 0.08)',
                  padding: '0.3rem 0.6rem',
                  borderRadius: '4px',
                  display: 'inline-block'
                }}>
                  Item: {rev.product}
                </div>
              )}
            </div>

            {/* Actions */}
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '0.6rem',
              paddingTop: '0.8rem',
              borderTop: '1px solid rgba(255, 255, 255, 0.05)'
            }}>
              <button
                onClick={() => openEditModal(rev)}
                className="btn btn-glass btn-sm"
                style={{ fontSize: '0.74rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}
              >
                <Edit3 size={13} /> Edit
              </button>
              <button
                onClick={() => deleteTestimonial(rev.id)}
                style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.74rem' }}
              >
                <Trash2 size={13} /> Remove
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Modal */}
      {modalOpen && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.85)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '1.5rem'
        }}>
          <div style={{
            width: '100%',
            maxWidth: '520px',
            backgroundColor: '#13131e',
            border: '1px solid var(--border-gold)',
            borderRadius: 'var(--radius-md)',
            padding: '2rem',
            position: 'relative'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#fff' }}>
                {editingId ? 'Edit Review' : 'Add Patron Testimonial'}
              </h3>
              <button onClick={() => setModalOpen(false)} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer' }}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: 'var(--gold-400)', marginBottom: '0.3rem' }}>
                    Patron / Couple Name *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Radhika & Kunal"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="input-luxury"
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: 'var(--gold-400)', marginBottom: '0.3rem' }}>
                    Rating Stars (1 to 5)
                  </label>
                  <select
                    value={form.rating}
                    onChange={(e) => setForm({ ...form, rating: Number(e.target.value) })}
                    className="input-luxury"
                  >
                    <option value={5}>★★★★★ (5 Stars)</option>
                    <option value={4}>★★★★☆ (4 Stars)</option>
                    <option value={3}>★★★☆☆ (3 Stars)</option>
                  </select>
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: 'var(--gold-400)', marginBottom: '0.3rem' }}>
                  Occasion / City / Role
                </label>
                <input
                  type="text"
                  placeholder="e.g. Destination Wedding, Udaipur or Verified Buyer, Delhi"
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value })}
                  className="input-luxury"
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: 'var(--gold-400)', marginBottom: '0.3rem' }}>
                  Purchased Item (Optional)
                </label>
                <input
                  type="text"
                  placeholder="e.g. Padmavati Royal Kundan & Polki Choker"
                  value={form.product}
                  onChange={(e) => setForm({ ...form, product: e.target.value })}
                  className="input-luxury"
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: 'var(--gold-400)', marginBottom: '0.3rem' }}>
                  Testimonial Review Quote *
                </label>
                <textarea
                  rows={3}
                  required
                  placeholder="Write the patron feedback quote here..."
                  value={form.quote}
                  onChange={(e) => setForm({ ...form, quote: e.target.value })}
                  className="input-luxury"
                  style={{ resize: 'vertical' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: 'var(--gold-400)', marginBottom: '0.3rem' }}>
                  Photo Avatar URL
                </label>
                <input
                  type="url"
                  value={form.avatar}
                  onChange={(e) => setForm({ ...form, avatar: e.target.value })}
                  className="input-luxury"
                  style={{ marginBottom: '0.4rem' }}
                />
                <label className="btn btn-glass btn-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer' }}>
                  <Upload size={14} />
                  <span>Upload Patron Photo</span>
                  <input type="file" accept="image/*" onChange={handleFileUpload} style={{ display: 'none' }} />
                </label>
              </div>

              <div style={{ display: 'flex', gap: '0.8rem', marginTop: '1rem' }}>
                <button type="button" onClick={() => setModalOpen(false)} className="btn btn-glass" style={{ flex: 1 }}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-gold" style={{ flex: 1 }}>
                  Publish Review
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
